import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CheckIcon from '@mui/icons-material/Check';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getValueKey, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { type } from './types';
const WidgetFormYesNo = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const value = props.getData();
    const values = [
        { value: true, label: (_b = (_a = props.config.labels) === null || _a === void 0 ? void 0 : _a.yesLabel) !== null && _b !== void 0 ? _b : 'Yes' },
        { value: false, label: (_d = (_c = props.config.labels) === null || _c === void 0 ? void 0 : _c.noLabel) !== null && _d !== void 0 ? _d : 'No' },
    ];
    if (props.config.showNotSet) {
        values.push({ label: (_f = (_e = props.config.labels) === null || _e === void 0 ? void 0 : _e.notSetLabel) !== null && _f !== void 0 ? _f : 'Not Set' });
    }
    const optionIndex = values.findIndex((option) => option.value === value);
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    let alignItems;
    switch ((_g = props.config.style) === null || _g === void 0 ? void 0 : _g.align) {
        case 'left':
            alignItems = 'flex-start';
            break;
        case 'center':
            alignItems = 'center';
            break;
        case 'right':
            alignItems = 'flex-end';
            break;
    }
    const ariaLabelledBy = `yes-no-radio-title-${props.id}`;
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, error: !!error, disabled: props.config.disabled, sx: { alignItems } },
        props.config.label && (React.createElement(FormLabel, { error: !!error, disabled: props.config.disabled, id: ariaLabelledBy }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(RadioGroup, { "aria-labelledby": `${props.id}${props.config.label}`, row: (_h = props.config.style) === null || _h === void 0 ? void 0 : _h.inline, value: optionIndex !== -1 ? optionIndex : null, onChange: (event, value) => {
                    const newValue = values[parseInt(value)].value;
                    props.setData(newValue);
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                } }, values.map((value, index) => {
                var _a;
                return (React.createElement(FormControlLabel, { key: getValueKey(value.value), value: index, control: React.createElement(Radio, { tabIndex: props.editMode ? -1 : undefined, "data-test": value.value ? 'yes-radio' : 'no-radio', autoFocus: ((_a = (!props.editMode && props.config.autoFocus)) !== null && _a !== void 0 ? _a : false) && index === 0, inputProps: {
                            'aria-describedby': ariaDescribedBy,
                        } }), label: value.label, "aria-labelledby": ariaLabelledBy }));
            }))),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormYesNoEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Yes No' + (props.config.label ? ': ' + props.config.label : ''),
        }, buildGeneralTab(props, {
            key: true,
            labelOptional: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.text',
                    key: 'labels.yesLabel',
                    label: 'Yes label',
                    emptyAsNull: true,
                },
                {
                    type: 'form.text',
                    key: 'labels.noLabel',
                    label: 'No label',
                    emptyAsNull: true,
                },
                {
                    type: 'form.text',
                    key: 'labels.notSetLabel',
                    label: 'Not set label',
                    emptyAsNull: true,
                },
                {
                    type: 'form.checkbox',
                    key: 'showNotSet',
                    label: 'Show not set option',
                },
                {
                    type: 'form.radio',
                    key: 'defaultValue',
                    label: 'Default value',
                    values: [{ label: 'Not Selected' }, { value: true, label: 'Yes' }, { value: false, label: 'No' }],
                    style: {
                        inline: true,
                    },
                },
            ],
        }), buildValidateTab(props, {
            required: true,
        }), buildHiddenTab(props), buildStyleTab(props, {
            align: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'style.inline',
                    label: 'Inline',
                },
            ],
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormYesNo, Object.assign({}, props))));
};
export const widgetFormYesNo = {
    type,
    init: (props) => {
        if (props.getData() == null && props.config.defaultValue != null) {
            props.setData(props.config.defaultValue);
        }
    },
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getData();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value == null) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: (props) => {
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        var _a, _b, _c, _d, _e, _f;
        const value = props.getData();
        let stringValue;
        if (value == null) {
            if (props.config.showNotSet) {
                stringValue = (_b = (_a = props.config.labels) === null || _a === void 0 ? void 0 : _a.notSetLabel) !== null && _b !== void 0 ? _b : 'Not Set';
            }
        }
        else {
            stringValue = value ? ((_d = (_c = props.config.labels) === null || _c === void 0 ? void 0 : _c.yesLabel) !== null && _d !== void 0 ? _d : 'Yes') : ((_f = (_e = props.config.labels) === null || _e === void 0 ? void 0 : _e.noLabel) !== null && _f !== void 0 ? _f : 'No');
        }
        return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), stringValue);
    },
    component: WidgetFormYesNo,
    icon: React.createElement(CheckIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormYesNoEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, {
        type,
        key: 'yesNo',
        label: 'Yes/No',
        style: { inline: true },
    }),
};
